import { Ionicons } from '@expo/vector-icons';
import React, { useEffect, useState } from 'react';
import { View, ScrollView, ActivityIndicator, TouchableOpacity } from 'react-native';

import { DSText } from '../../../design-system/components/DSText';
import { useAppTheme } from '../../../design-system/ThemeProvider';
import { spacing, radius, shadows } from '../../../design-system/tokens';
import { t } from '../../../i18n/translations';
import { isScreenshotMode, isScreenshotGuestMode, DEMO_FAMILY_LEADERBOARD } from '../../../../lib/screenshotMode';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../shared/hooks/useAuth';
import { useLanguageStore } from '../../quiz-core/languageStore';
import { uiText } from '../../../shared/utils/uiText';

interface LeaderboardEntry {
  user_id: string;
  username: string;
  email: string;
  total_points: number;
  quizzes_completed: number;
  correct_answers: number;
  accuracy_rate: number;
  streak_days: number;
  rank: number;
  achievement_badges: string[];
  family_names: string[];
}

interface FamilyLeaderboardProps {
  familyIds: string[];
  currentUserId: string;
}

export const FamilyLeaderboard: React.FC<FamilyLeaderboardProps> = ({ familyIds, currentUserId }) => {
  const { colors } = useAppTheme();
  const { language } = useLanguageStore();
  // Store screenshot capture: seed demo standings, skip network fetch
  const screenshotMode = isScreenshotMode() && !isScreenshotGuestMode();
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>(
    screenshotMode ? (DEMO_FAMILY_LEADERBOARD as LeaderboardEntry[]) : []
  );
  const [loading, setLoading] = useState(!screenshotMode);
  const [timeFilter, setTimeFilter] = useState<'week' | 'month' | 'all'>('month');
  const { user } = useAuth();

  useEffect(() => {
    if (screenshotMode) return;
    if (familyIds.length > 0) {
      fetchLeaderboard();
    }
  }, [familyIds, timeFilter]);

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      // Get all members from the user's families
      const { data: membersData, error: membersError } = await supabase
        .from('family_members')
        .select(`
          user_id,
          family_id,
          families!inner(name)
        `)
        .in('family_id', familyIds);

      if (membersError) {
        setLoading(false);
        return;
      }

      if (!membersData || membersData.length === 0) {
        setLeaderboard([]);
        setLoading(false);
        return;
      }

      // Get unique user IDs
      const userIds = [...new Set(membersData.map(m => m.user_id))];

      // Get user profiles (including name field as fallback)
      const { data: profiles, error: profilesError } = await supabase
        .from('user_profiles')
        .select('id, username, name, email')
        .in('id', userIds);

      // Get quiz statistics for each user
      let dateFilter = new Date();
      if (timeFilter === 'week') {
        dateFilter.setDate(dateFilter.getDate() - 7);
      } else if (timeFilter === 'month') {
        dateFilter.setMonth(dateFilter.getMonth() - 1);
      } else {
        dateFilter = new Date('2020-01-01'); // All time
      }

      // Get single player quiz results
      const { data: singlePlayerStats, error: singlePlayerError } = await supabase
        .from('singleplayer_results')
        .select('user_id, score, total_questions, accuracy, created_at')
        .in('user_id', userIds)
        .gte('created_at', dateFilter.toISOString());

      // Get multiplayer quiz results
      const { data: multiplayerStats, error: multiplayerError } = await supabase
        .from('multiplayer_game_room_players')
        .select('user_id, score, total_questions, finished_at')
        .in('user_id', userIds)
        .gte('finished_at', dateFilter.toISOString())
        .not('finished_at', 'is', null) // Only completed games
        .not('score', 'is', null); // Only games with scores

      // Combine both single player and multiplayer stats
      const quizStats = [
        ...(singlePlayerStats || []),
        ...(multiplayerStats || []).map(mp => ({
          user_id: mp.user_id,
          score: mp.score || 0,
          total_questions: mp.total_questions || 10,
          accuracy: (mp.total_questions && mp.total_questions > 0)
            ? (mp.score || 0) / mp.total_questions * 100
            : 0,
          created_at: mp.finished_at
        }))
      ];

      // Calculate leaderboard entries
      const leaderboardData: LeaderboardEntry[] = userIds.map(userId => {
        const profile = profiles?.find(p => p.id === userId);
        const userQuizzes = quizStats?.filter(q => q.user_id === userId) || [];
        const userFamilies = membersData
          .filter(m => m.user_id === userId)
          .map(m => (m as any).families?.name || 'Unknown');

        // Calculate stats from actual data
        const totalQuizzes = userQuizzes.length;
        const totalCorrect = userQuizzes.reduce((sum, q) => sum + (q.score || 0), 0);
        const totalQuestions = userQuizzes.reduce((sum, q) => sum + (q.total_questions || 0), 0);

        // Use the accuracy from the database or calculate it
        const avgAccuracy = totalQuizzes > 0
          ? userQuizzes.reduce((sum, q) => sum + (q.accuracy || 0), 0) / totalQuizzes
          : 0;

        // Calculate points based on actual performance
        const basePoints = totalQuizzes > 0 ? 50 : 0; // Base points for having played
        const quizPoints = totalQuizzes * 20; // 20 points per quiz completed
        const accuracyBonus = Math.floor(avgAccuracy * 10); // Up to 1000 points for 100% accuracy
        const streakBonus = 0; // TODO: Implement streak calculation

        const totalPoints = basePoints + quizPoints + totalCorrect + accuracyBonus + streakBonus;

        // Calculate achievement badges based on real performance
        const badges: string[] = [];
        if (totalQuizzes >= 100) badges.push('centurion');
        else if (totalQuizzes >= 50) badges.push('dedicated');
        else if (totalQuizzes >= 10) badges.push('starter');

        if (avgAccuracy >= 90) badges.push('perfectionist');
        else if (avgAccuracy >= 75) badges.push('scholar');

        if (totalCorrect >= 500) badges.push('master');
        else if (totalCorrect >= 250) badges.push('expert');
        else if (totalCorrect >= 100) badges.push('achiever');

        return {
          user_id: userId,
          username: profile?.username || profile?.name || profile?.email?.split('@')[0] || 'Anonymous',
          email: profile?.email || '',
          total_points: totalPoints,
          quizzes_completed: totalQuizzes,
          correct_answers: totalCorrect,
          accuracy_rate: avgAccuracy,
          streak_days: 0, // TODO: Calculate from user_streaks table
          rank: 0,
          achievement_badges: badges,
          family_names: userFamilies
        };
      });

      // Sort by points and assign ranks
      leaderboardData.sort((a, b) => b.total_points - a.total_points);
      leaderboardData.forEach((entry, index) => {
        entry.rank = index + 1;
      });

      setLeaderboard(leaderboardData);
    } catch (error) {
      // Silently handle error — loading state will reset
    } finally {
      setLoading(false);
    }
  };

  const getBadgeIcon = (badge: string): { name: keyof typeof Ionicons.glyphMap; color: string } => {
    switch (badge) {
      case 'centurion': return { name: 'trophy', color: '#F59E0B' };
      case 'dedicated': return { name: 'star', color: '#4F46E5' };
      case 'starter': return { name: 'sparkles', color: '#6366F1' };
      case 'perfectionist': return { name: 'checkmark-circle', color: '#10B981' };
      case 'scholar': return { name: 'book', color: '#3B82F6' };
      case 'master': return { name: 'shield-checkmark', color: '#8B5CF6' };
      case 'expert': return { name: 'ribbon', color: '#F59E0B' };
      case 'achiever': return { name: 'medal', color: '#F59E0B' };
      default: return { name: 'star-outline', color: '#777587' };
    }
  };

  // Calculate total family stats for the warm header
  const totalFamilyQuizzes = leaderboard.reduce((sum, e) => sum + (Number(e.quizzes_completed) || 0), 0);
  const totalFamilyAnswers = leaderboard.reduce((sum, e) => sum + (Number(e.correct_answers) || 0), 0);

  if (loading) {
    return (
      <View style={[
        {
          backgroundColor: colors.surfaceContainerLow,
          borderWidth: 1,
          borderColor: colors.outlineVariant,
          borderRadius: radius.xl,
          padding: spacing['2xl'],
        },
        shadows.lg,
      ]}>
        <DSText variant="title" color={colors.onSurface} style={{ marginBottom: spacing.lg }}>
          {t('gamification.leaderboard', language)}
        </DSText>
        <View style={{ alignItems: 'center', paddingVertical: spacing['4xl'] }}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      </View>
    );
  }

  if (leaderboard.length === 0) {
    return (
      <View style={[
        {
          backgroundColor: colors.surfaceContainerLow,
          borderWidth: 1,
          borderColor: colors.outlineVariant,
          borderRadius: radius.xl,
          padding: spacing['2xl'],
        },
        shadows.lg,
      ]}>
        <DSText variant="title" color={colors.onSurface} style={{ marginBottom: spacing.lg }}>
          {t('gamification.leaderboard', language)}
        </DSText>
        <DSText
          variant="body"
          color={colors.onSurfaceVariant}
          align="center"
          style={{ paddingVertical: spacing['4xl'] }}
        >
          {t('familiesFriends.familyQuizSubtitle', language)}
        </DSText>
      </View>
    );
  }

  return (
    <View style={[
      {
        backgroundColor: colors.surfaceContainerLow,
        borderRadius: radius.xl,
        overflow: 'hidden',
        borderWidth: 1,
        borderColor: colors.outlineVariant,
      },
      shadows.lg,
    ]}>
      {/* Warm header with family aggregate stats */}
      <View style={{ backgroundColor: colors.surfaceContainerLow, padding: spacing.xl, borderBottomWidth: 1, borderBottomColor: colors.outlineVariant }}>
        <DSText variant="title" color={colors.onSurface}>{t('gamification.leaderboard', language)}</DSText>
        <DSText
          variant="body"
          color={colors.onSurfaceVariant}
          style={{ marginTop: spacing.sm }}
        >
          {uiText(timeFilter === 'week' ? 'exploredWeek' : timeFilter === 'month' ? 'exploredMonth' : 'exploredAll', { count: totalFamilyAnswers.toLocaleString() })}
        </DSText>
        <DSText
          variant="caption"
          color={colors.outline}
          style={{ marginTop: spacing.xs }}
        >
          {totalFamilyQuizzes} quizzes completed together
        </DSText>
      </View>

      {/* Time Filter */}
      <View style={{
        flexDirection: 'row',
        justifyContent: 'center',
        padding: spacing.md,
        backgroundColor: colors.surfaceDim,
        borderBottomWidth: 1,
        borderBottomColor: colors.outlineVariant,
      }}>
        {(['week', 'month', 'all'] as const).map((filter) => (
          <TouchableOpacity
            key={filter}
            onPress={() => setTimeFilter(filter)}
            style={{
              paddingHorizontal: spacing.lg,
              paddingVertical: spacing.sm,
              marginHorizontal: spacing.xs,
              borderRadius: radius.full,
              backgroundColor: timeFilter === filter ? colors.primary : colors.surfaceContainerHigh,
              borderWidth: timeFilter === filter ? 0 : 1,
              borderColor: colors.outlineVariant,
            }}
          >
            <DSText
              variant="label"
              color={timeFilter === filter ? colors.onPrimary : colors.onSurfaceVariant}
            >
              {uiText(filter === 'week' ? 'filterWeek' : filter === 'month' ? 'filterMonth' : 'filterAll')}
            </DSText>
          </TouchableOpacity>
        ))}
      </View>

      {/* Family members — warm contribution view */}
      <ScrollView style={{ maxHeight: 384 }}>
        {leaderboard.map((entry) => {
          const isCurrentUser = entry.user_id === currentUserId;

          return (
            <View
              key={entry.user_id}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                padding: spacing.lg,
                borderBottomWidth: 1,
                borderBottomColor: colors.surfaceContainerLow,
                backgroundColor: isCurrentUser ? colors.primaryContainer : colors.transparent,
              }}
            >
              {/* Avatar circle with initial */}
              <View style={{
                width: 40,
                height: 40,
                borderRadius: radius.full,
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: spacing.md,
                backgroundColor: isCurrentUser ? colors.primary : colors.primaryContainer,
              }}>
                <DSText
                  variant="body"
                  color={isCurrentUser ? colors.onPrimary : colors.primaryDark}
                  style={{ fontWeight: '700' }}
                >
                  {entry.username.charAt(0).toUpperCase()}
                </DSText>
              </View>

              {/* User Info */}
              <View style={{ flex: 1 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <DSText
                    variant="label"
                    color={isCurrentUser ? colors.primaryDark : colors.onSurface}
                    style={isCurrentUser ? { fontSize: 16 } : undefined}
                  >
                    {entry.username}
                    {isCurrentUser && ' (You)'}
                  </DSText>
                  {/* Badges */}
                  <View style={{ flexDirection: 'row', marginLeft: spacing.sm }}>
                    {entry.achievement_badges.slice(0, 3).map((badge, i) => {
                      const icon = getBadgeIcon(badge);
                      return (
                        <Ionicons
                          key={i}
                          name={icon.name}
                          size={14}
                          color={icon.color}
                          style={{ marginLeft: spacing.xs }}
                        />
                      );
                    })}
                  </View>
                </View>

                {/* Warm stats — collaborative framing */}
                <DSText
                  variant="caption"
                  color={colors.onSurfaceVariant}
                  style={{ marginTop: 2 }}
                >
                  {entry.quizzes_completed > 0
                    ? uiText('entryStats', { questions: entry.correct_answers, quizzes: entry.quizzes_completed })
                    : uiText('readyToStart')}
                </DSText>
              </View>

              {/* Contribution indicator */}
              <View style={{ alignItems: 'flex-end' }}>
                <DSText variant="label" color={colors.primary}>
                  {entry.accuracy_rate > 0 ? `${entry.accuracy_rate.toFixed(0)}%` : '—'}
                </DSText>
                <DSText variant="micro" color={colors.outline}>accuracy</DSText>
              </View>
            </View>
          );
        })}
      </ScrollView>

      {/* Warm footer — family encouragement */}
      <View style={{
        backgroundColor: colors.primaryContainer,
        padding: spacing.lg,
        borderTopWidth: 1,
        borderTopColor: colors.outlineVariant,
      }}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-around' }}>
          <View style={{ alignItems: 'center' }}>
            <DSText variant="heading" color={colors.primaryDark}>
              {totalFamilyQuizzes}
            </DSText>
            <DSText variant="micro" color={colors.onSurfaceVariant} align="center">
              {uiText('statQuizzesTogether')}
            </DSText>
          </View>
          <View style={{ alignItems: 'center' }}>
            <DSText variant="heading" color={colors.primaryDark}>
              {totalFamilyAnswers}
            </DSText>
            <DSText variant="micro" color={colors.onSurfaceVariant} align="center">
              {uiText('statQuestionsExplored')}
            </DSText>
          </View>
          <View style={{ alignItems: 'center' }}>
            <DSText variant="heading" color={colors.primaryDark}>
              {leaderboard.filter(e => e.quizzes_completed > 0).length}/{leaderboard.length}
            </DSText>
            <DSText variant="micro" color={colors.onSurfaceVariant} align="center">
              {uiText('statMembersActive')}
            </DSText>
          </View>
        </View>
      </View>
    </View>
  );
};

export default FamilyLeaderboard;
